import {useState, useEffect} from "react"
import {MessageFilter} from "@/features/rulesets"
import {isStubEnabled} from "@/config/stubConfig"
import {useStubFilters} from "./useStubFilters"
import supabase from "@/supabaseConfig"
import {useUser} from "@/contexts/UserContextProvider"

/**
 * Custom hook to fetch and manage organization filters directly from Supabase
 *
 * @returns Loading state, error state, and filter data
 */
export function useFilters(explicitOrgId?: string) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [data, setData] = useState<MessageFilter[]>([])

  // Get the organization ID from context if not explicitly provided
  const {orgId: contextOrgId} = useUser()

  // Use provided orgId or fallback to context orgId
  const orgId = explicitOrgId || contextOrgId

  // Get stub data hook
  const stubFilters = useStubFilters()

  // Check if we should use stub data
  const useStubData = isStubEnabled("filters")

  useEffect(() => {
    // If using stub data, return early
    if (useStubData) {
      setLoading(false)
      return
    }

    // If no orgId, don't attempt to fetch data
    if (!orgId) {
      setLoading(false)
      return
    }

    // Variable to track if the component is mounted
    let isMounted = true

    // Fetch the filters directly from Supabase
    const loadFilters = async () => {
      try {
        setLoading(true)

        // Direct Supabase query
        console.log("Checking filters for:", orgId)

        const {data, error} = await supabase
          .from("message_filters")
          .select("*")
          .eq("org_id", orgId)
          .order("filter_index", {ascending: true})

        console.log("All filters in table:", data)
        console.log("Query error if any:", error)

        // Handle Supabase error
        if (error) {
          throw new Error(`Failed to fetch filters: ${error.message}`)
        }

        // Only update state if the component is still mounted
        if (isMounted) {
          setData((data as MessageFilter[]) || [])
          setError(null)
        }
      } catch (err) {
        // Only update state if the component is still mounted
        if (isMounted) {
          setError(
            err instanceof Error
              ? err
              : new Error("Unknown error fetching filters")
          )
          console.error("Error loading filters:", err)
        }
      } finally {
        // Only update state if the component is still mounted
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    // Call the fetch function
    loadFilters()

    // Cleanup function to handle component unmounting
    return () => {
      isMounted = false
    }
  }, [orgId, useStubData])

  // Return stub data if enabled, otherwise return real data
  return useStubData ? stubFilters : {loading, error, data}
}
